import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

export interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  keywords?: string;
  schema?: Record<string, any> | Array<Record<string, any>>;
  noindex?: boolean;
}

const DOMAIN = "https://www.25thandstaffing.com";
const DEFAULT_TITLE = "25th & Staffing — Retained Executive Search, Rooted in Africa";
const DEFAULT_DESCRIPTION =
  "Human-led, AI-powered retained search and workforce solutions. Eleven years placing senior talent globally. Executive search, permanent staffing, and workforce technology.";
const DEFAULT_IMAGE =
  "https://res.cloudinary.com/defwvlcup/image/upload/v1674636928/25thandstaffing/icon_black_yvmoug.png";

// Global Organization Schema required on all pages
const ORGANIZATION_SCHEMA: Record<string, any> = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "25th & Staffing",
  "url": DOMAIN,
  "logo": DEFAULT_IMAGE,
  "description":
    "Human-led, AI-powered retained executive search and workforce solutions company. Rooted in Africa, reaching anywhere.",
  "foundingDate": "2013",
  "slogan": "Human-led, AI-powered retained search, rooted in Africa, reaching anywhere.",
  "areaServed": "Worldwide",
  "knowsAbout": [
    "Executive Search",
    "Retained Search",
    "Permanent Staffing",
    "Recruitment Services",
    "AI Recruitment Technology",
    "Workforce Management",
    "HR Software",
    "Payroll Software"
  ],
  "sameAs": [
    "https://www.linkedin.com/company/25thandstaffing",
    "https://twitter.com/25thandstaffing"
  ]
};

export const SEO: React.FC<SEOProps> = ({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  canonical,
  ogImage = DEFAULT_IMAGE,
  ogType = "website",
  keywords,
  schema,
  noindex = false,
}) => {
  const location = useLocation();
  const currentCanonical =
    canonical || `${DOMAIN}${location.pathname === "/" ? "" : location.pathname}`;

  const schemasToRender: Array<Record<string, any>> = [ORGANIZATION_SCHEMA];

  if (schema) {
    if (Array.isArray(schema)) {
      schemasToRender.push(...schema);
    } else {
      schemasToRender.push(schema);
    }
  }

  React.useEffect(() => {
    if (typeof document !== "undefined") {
      document.title = title;

      // Ensure only one meta[name="description"] exists and has current description
      const metaDescriptions = document.querySelectorAll('meta[name="description"]');
      if (metaDescriptions.length > 0) {
        metaDescriptions[0].setAttribute("content", description);
        // Remove any extra duplicates
        for (let i = 1; i < metaDescriptions.length; i++) {
          metaDescriptions[i].remove();
        }
      } else {
        const meta = document.createElement("meta");
        meta.setAttribute("name", "description");
        meta.setAttribute("content", description);
        document.head.appendChild(meta);
      }

      // Sync OpenGraph and Twitter meta tags in DOM
      const syncMetaTag = (attrName: string, attrVal: string, content: string) => {
        let el = document.querySelector(`meta[${attrName}="${attrVal}"]`);
        if (!el) {
          el = document.createElement("meta");
          el.setAttribute(attrName, attrVal);
          document.head.appendChild(el);
        }
        el.setAttribute("content", content);
      };

      syncMetaTag("property", "og:title", title);
      syncMetaTag("property", "og:description", description);
      syncMetaTag("property", "og:url", currentCanonical);
      syncMetaTag("property", "og:image", ogImage);
      syncMetaTag("name", "twitter:title", title);
      syncMetaTag("name", "twitter:description", description);
      syncMetaTag("name", "twitter:image", ogImage);

      const canonicalLink = document.querySelector('link[rel="canonical"]');
      if (canonicalLink) {
        canonicalLink.setAttribute("href", currentCanonical);
      }
    }
  }, [title, description, currentCanonical, ogImage]);

  return (
    <Helmet>
      {/* Basic HTML Meta Tags */}
      <title>{title}</title>
      <meta data-rh="true" name="description" content={description} />
      {keywords && <meta data-rh="true" name="keywords" content={keywords} />}
      {noindex ? (
        <meta data-rh="true" name="robots" content="noindex, nofollow" />
      ) : (
        <meta data-rh="true" name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
      <link data-rh="true" rel="canonical" href={currentCanonical} />

      {/* OpenGraph / Facebook Tags */}
      <meta data-rh="true" property="og:site_name" content="25th & Staffing" />
      <meta data-rh="true" property="og:type" content={ogType} />
      <meta data-rh="true" property="og:title" content={title} />
      <meta data-rh="true" property="og:description" content={description} />
      <meta data-rh="true" property="og:url" content={currentCanonical} />
      <meta data-rh="true" property="og:image" content={ogImage} />

      {/* Twitter Card Tags */}
      <meta data-rh="true" name="twitter:card" content="summary_large_image" />
      <meta data-rh="true" name="twitter:site" content="@25thandstaffing" />
      <meta data-rh="true" name="twitter:title" content={title} />
      <meta data-rh="true" name="twitter:description" content={description} />
      <meta data-rh="true" name="twitter:image" content={ogImage} />

      {/* Structured Data / JSON-LD Schemas */}
      {schemasToRender.map((s, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
};


export default SEO;
